import { FC, useState, useEffect } from "react";
import { useFamilyRequests } from "../../../../hooks/useFamilyRequests";
import { RequestActionModal } from "./RequestActionModal/RequestActionModal";
import styles from './FamilyGroupRequests.module.css';
import { Title } from "../../../UI/Title/Title";
import iconEdit from '../../../../assets/img/icons/angle-right.png';
import { Loading, Spacer, Text } from "../../../UI";

interface SelectedRequest {
    id: number;
    applicantId: number;
    familyId: number;
    applicantName: string;
    relationship: string;
}

export const FamilyGroupRequests: FC<{onFamilyChange: () => void}> = ({onFamilyChange}) => {
    const { sentRequests, receivedRequests, loading, error, handleAccept, handleReject } = useFamilyRequests();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<SelectedRequest | null>(null);

    const getRequestStatus = (status: number | boolean) => {
        switch(status) {
            case 0 || false:
                return 'Pendiente';
            case 1:
                return 'Aprobada';
            case 2:
                return 'Rechazada';
            default:
                return '';
        }
    }

    const reloadFamilyGroupAndNotifications = () => {
        onFamilyChange();
        // TODO: Implementar la actualizacion de notificaciones con redux y eliminar el reload
        window.location.reload();
    }

    const openActionModal = (request: any) => {
        setSelectedRequest({
            id: request.id,
            applicantId: request.applicant_patient.id,
            familyId: request.family_id,
            applicantName: `${request.applicant_patient.first_name} ${request.applicant_patient.last_name}`,
            relationship: request.type_relationship.name
        });
        setIsModalOpen(true);
    };

    const handleModalAccept = async () => {
        if (selectedRequest) {
            await handleAccept(
                selectedRequest.id,
                selectedRequest.applicantId,
                selectedRequest.familyId
            );
            setIsModalOpen(false);
            setSelectedRequest(null);
            reloadFamilyGroupAndNotifications();
        }
    };

    const handleModalReject = async () => {
        if (selectedRequest) {
            await handleReject(
                selectedRequest.id,
                selectedRequest.applicantId,
                selectedRequest.familyId
            );
            setIsModalOpen(false);
            setSelectedRequest(null);
            reloadFamilyGroupAndNotifications();
        }
    };

    const getStatusClass = (status: number | boolean) => {
        if (status === 0 || status === false) return styles.table__item__status__pending;
        if (status === 1) return styles.table__item__status__approved;
        if (status === 2) return styles.table__item__status__rejected;
        return '';
    };

    const filteredSentRequests = Array.isArray(sentRequests)
    ? sentRequests
        .filter(request => request.request_status !== 3)
        .sort((a, b) => new Date(b.request_creation_date).getTime() - new Date(a.request_creation_date).getTime())
    : [];

    const filteredReceivedRequests = Array.isArray(receivedRequests)
    ? receivedRequests
        .filter(request => request.request_status !== 3)
        .sort((a, b) => new Date(b.request_creation_date).getTime() - new Date(a.request_creation_date).getTime())
    : [];


    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const requestId = urlParams.get('request_id');

        if (requestId) {
            const isMobileView = window.innerWidth <= 860;
            const requestElement = isMobileView
                ? document.getElementById(`request-mobile-${requestId}`)
                : document.getElementById(`request-${requestId}`);

            if (requestElement) {
                const topPosition = requestElement.getBoundingClientRect().top + window.pageYOffset - 100;
                window.scrollTo({ top: topPosition, behavior: 'smooth' });

                requestElement.classList.add(styles.highlight);
                setTimeout(() => {
                    requestElement.classList.remove(styles.highlight);
                }, 3000);
            }
        }
    }, [filteredReceivedRequests]);

    if (loading) return <div className={styles.loading__container}><Loading/></div>

    const hasReceivedRequests = filteredReceivedRequests.length > 0;
    const hasSentRequests = filteredSentRequests.length > 0;

    return (
        <div>
            <Title component="h2" size="20" weight='600'>
                Solicitudes Recibidas
            </Title>
            <Spacer size="medium"/>
            {hasReceivedRequests ? (
                <div className={styles.table__container__desktop}>
                    <table className={styles.table}>
                        <thead className={styles.table__header}>
                        <tr>
                            <th className={styles.table__header__item}>Fecha</th>
                            <th className={styles.table__header__item}>Hora</th>
                            <th className={styles.table__header__item}>Solicitante</th>
                            <th className={styles.table__header__item}>Tipo de relación</th>
                            <th className={styles.table__header__item}>Estado</th>
                            <th className={styles.table__header__item}></th>
                        </tr>
                        </thead>
                        <tbody className={styles.table__body}>
                            {filteredReceivedRequests.map((request) => (
                                <tr key={request.id} id={`request-${request.id}`} className={styles.table__row}>
                                    <td className={styles.table__item}>{new Date(request.request_creation_date).toLocaleDateString()}</td>
                                    <td className={styles.table__item}>{new Date(request.request_creation_date).toLocaleTimeString()}</td>
                                    <td className={styles.table__item}>{`${request?.applicant_patient?.first_name ?? ''} ${request?.applicant_patient?.last_name ?? ''}`}</td>
                                    <td className={styles.table__item}>{request?.type_relationship?.name}</td>
                                    <td className={styles.table__item}>
                                        <span
                                            className={`${styles.table__item__status} ${
                                                request.request_status === 0 || request.request_status === false
                                                    ? `${styles.table__item__status__pending} ${styles.table__item__status_clickable}`
                                                    : request.request_status === 1
                                                    ? styles.table__item__status__approved
                                                    : styles.table__item__status__rejected
                                            }`}
                                            onClick={() => {
                                                if (request.request_status === 0 || request.request_status === false) {
                                                    openActionModal(request);
                                                }
                                            }}
                                        >
                                            {getRequestStatus(request.request_status)}
                                        </span>
                                    </td>
                                    <td className={`${styles.table__item__action} ${styles.table__item}`}>
                                        {request.request_status === 0 || request.request_status === false && (
                                            <img className={styles.table__item__icon} src={iconEdit} onClick={() => openActionModal(request)} alt="Editar" />
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className={styles.no__requests__container}>
                    <Text>Sin solicitudes</Text>
                    <Spacer size="medium"/>
                </div>
            )}

            <div className={styles.table__container__mobile}>
                {hasReceivedRequests && (
                    filteredReceivedRequests.map((request) => (
                        <div key={request.id} id={`request-mobile-${request.id}`} className={styles.table__item__mobile}>
                            <div className={styles.info__item__header}>
                                {
                                    <span
                                    className={`${styles.table__item__status} ${
                                        request.request_status === 0 || request.request_status === false
                                            ? `${styles.table__item__status__pending} ${styles.table__item__status_clickable}`
                                            : request.request_status === 1
                                            ? styles.table__item__status__approved
                                            : styles.table__item__status__rejected
                                    }`}
                                    onClick={() => {
                                            if (request.request_status === 0 || request.request_status === false) {
                                                openActionModal(request);
                                            }
                                        }}
                                    >
                                        {getRequestStatus(request.request_status)}
                                    </span>
                                }
                                
                                
                                {request.request_status === 0 || request.request_status === false && (  
                                    <img src={iconEdit} alt="Editar" className={styles.table__item__icon} onClick={() => openActionModal(request)}/>
                                )}
                            </div>
                            <div className={styles.info__item}>
                                <p className={styles.info__label}>Solicitante</p>
                                <p className={styles.info__value}>{`${request?.applicant_patient?.first_name} ${request?.applicant_patient?.last_name}`}</p>
                            </div>
                            <div className={styles.info__item}>
                                <p className={styles.info__label}>Tipo de relación</p>
                                <p className={styles.info__value}>{request?.type_relationship?.name}</p>
                            </div>
                            <div className={styles.info__item}>
                                <p className={styles.info__label}>Fecha</p>
                                <p className={styles.info__value}>
                                    {new Date(request.request_creation_date).toLocaleDateString()} - {new Date(request.request_creation_date).toLocaleTimeString()}
                                </p>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <Spacer size="medium"/>
            <Title component="h2" size="20" weight='600'>
                Solicitudes Enviadas
            </Title>
            <Spacer size="medium"/>
            {hasSentRequests ? (
                <div className={styles.table__container__desktop}>
                    <table className={styles.table}>
                        <thead className={styles.table__header}>
                        <tr>
                            <th className={styles.table__header__item}>Fecha</th>
                            <th className={styles.table__header__item}>Hora</th>
                            <th className={styles.table__header__item}>Receptor</th>
                            <th className={styles.table__header__item}>Tipo de relación</th>
                            <th className={styles.table__header__item}>Estado</th>
                        </tr>
                        </thead>
                        <tbody className={styles.table__body}>
                            {filteredSentRequests.map((request) => (
                                <tr key={request.id} id={`request-${request.id}`} className={styles.table__row}>
                                    <td className={styles.table__item}>{new Date(request.request_creation_date).toLocaleDateString()}</td>
                                    <td className={styles.table__item}>{new Date(request.request_creation_date).toLocaleTimeString()}</td>
                                    <td className={styles.table__item}>{`${request?.family_patient?.first_name ?? request?.family_member?.first_name ?? ''} ${request?.family_patient?.last_name ?? request?.family_member?.last_name ?? ''}`}</td>
                                    <td className={styles.table__item}>{request?.type_relationship?.name}</td>
                                    <td className={styles.table__item}>
                                        <span className={`${styles.table__item__status} ${getStatusClass(request.request_status)}`}>
                                            {getRequestStatus(request.request_status)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className={styles.no__requests__container}>
                    <Text>Sin solicitudes</Text>
                    <Spacer size="medium"/>
                </div>
            )}

            <div className={styles.table__container__mobile}>
                {hasSentRequests && (
                    filteredSentRequests.map((request) => (
                        <div key={request.id} id={`request-mobile-${request.id}`} className={styles.table__item__mobile} >
                            <div className={styles.info__item__header}>
                                <span className={`${styles.table__item__status} ${getStatusClass(request.request_status)}`}>
                                    {getRequestStatus(request.request_status)}
                                </span>
                            </div>
                            <div className={styles.info__item}>
                                <p className={styles.info__label}>Receptor</p>
                                <p className={styles.info__value}>{`${request?.family_patient?.first_name ?? request?.family_member?.first_name ?? ''} ${request?.family_patient?.last_name ?? request?.family_member?.last_name ?? ''}`}</p>
                            </div>
                            <div className={styles.info__item}>
                                <p className={styles.info__label}>Tipo de relación</p>
                                <p className={styles.info__value}>{request?.type_relationship?.name}</p>
                            </div>
                            <div className={styles.info__item}>
                                <p className={styles.info__label}>Fecha</p>
                                <p className={styles.info__value}>
                                    {new Date(request.request_creation_date).toLocaleDateString()} - {new Date(request.request_creation_date).toLocaleTimeString()}
                                </p>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <RequestActionModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedRequest(null);
                }}
                onAccept={handleModalAccept}
                onReject={handleModalReject}
                requestInfo={selectedRequest ? {
                    applicantName: selectedRequest.applicantName,
                    relationship: selectedRequest.relationship
                } : { applicantName: '', relationship: '' }}
            />
        </div>
    );
};
